import {
  writeBatch, doc, updateDoc, setDoc, deleteDoc,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { assignTerritory } from '../../utils/pointInPolygon';

// Polygon vertices are stored as {lat,lng} maps (Firestore forbids nested arrays).
export const encodePolygon = (latlngs) => (latlngs || []).map((p) => ({ lat: p.lat ?? p[0], lng: p.lng ?? p[1] }));

// Edit a territory (shape/color/rollout/active). Owner/HQ per rules.
export function updateTerritory(territoryId, patch, actor) {
  return updateDoc(doc(db, 'territories', territoryId), {
    ...patch,
    updatedBy: actor?.uid ?? null,
    updatedAt: Date.now(),
  });
}

// Create a new territory (drawn polygon). Owner/HQ; tenantId must be in scope.
export function createTerritory(id, {
  tenantId, storeId, label, name, color, rollout, polygon,
}, actor) {
  const now = Date.now();
  return setDoc(doc(db, 'territories', id), {
    tenantId,
    storeId: storeId ?? null,
    label: label ?? id,
    name: name ?? label ?? id,
    color: color ?? '#7b3fb8',
    rollout: rollout ?? 99,
    active: true,
    polygon: polygon ?? [],
    createdBy: actor?.uid ?? null,
    createdAt: now,
    updatedAt: now,
  });
}

// Delete a territory (owner/HQ per rules). Leads keep their territoryId; the
// caller should re-run reassignLeads against the remaining territories so any
// orphaned leads move to a zone that still exists.
export function deleteTerritory(territoryId) {
  return deleteDoc(doc(db, 'territories', territoryId));
}

// Pure: given leads + current territory polygons, compute which leads need their
// territoryId changed (e.g. after a polygon edit). Leads without coords or that
// fall outside every polygon are left untouched.
export function computeReassignments(leads, territories) {
  const changes = [];
  for (const l of leads || []) {
    if (typeof l.lat !== 'number' || typeof l.lng !== 'number') continue;
    const to = assignTerritory([l.lat, l.lng], territories);
    if (to && to !== l.territoryId) {
      changes.push({ leadId: l.id, from: l.territoryId ?? null, to });
    }
  }
  return changes;
}

// Apply reassignments in one batch. Stamps updatedBy so onLeadWrite records the
// actor (DoR-27b). Returns the count written.
export async function applyReassignments(changes, actor) {
  if (!changes || changes.length === 0) return 0;
  const batch = writeBatch(db);
  const ts = Date.now();
  for (const c of changes) {
    batch.update(doc(db, 'leads', c.leadId), {
      territoryId: c.to,
      updatedBy: actor?.uid ?? null,
      updatedByName: actor?.name ?? null,
      updatedAt: ts,
    });
  }
  await batch.commit();
  return changes.length;
}

// Convenience: recompute + apply for a set of leads against current territories.
export function reassignLeads(leads, territories, actor) {
  return applyReassignments(computeReassignments(leads, territories), actor);
}
